<script setup lang="ts">
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout.vue'
import NonAuthenticatedLayout from '@/components/layout/NonAuthenticatedLayout.vue'
import ExternalLinkModal from './components/modals/ExternalLinkModal.vue'
import { isExternalLink } from '@/util/links/externalLinks'
import { Toaster } from '@/components/shad/ui/sonner'
import useAccountEnabled from '@/composables/useAccountEnabled'
import useDarkMode from '@/composables/useDarkMode.store'
import useVersionCheck from '@/composables/useVersionCheck'
import useAuthStore from '@/stores/auth'

import { onMounted, onUnmounted, toRefs, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const authStore = useAuthStore()
const darkMode = useDarkMode()
const { isDark } = toRefs(darkMode)
const route = useRoute()
const router = useRouter()

const externalLinkOpen = ref(false)
const externalLinkUrl = ref<string | undefined>(undefined)

/**
 * This checks whether the version deployed on the server still matches the one
 * this tab is running. If not, it forces a reload.
 */
useVersionCheck()

/**
 * This watches for the signed-in account having been disabled, which the API
 * cannot report by rejecting their token, and sends them to the disabled page.
 */
useAccountEnabled()

/**
 * This event handler will open the MITRE technique modal window when any HTML element has the attribute
 * 'data-mitretechnique'.
 * @example `<div data-mitretechnique="T1595">Click here to open modal for technique T1595</div>`
 */
const onClickOfElWithMitreTechniqueAttr = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const mitreTechniqueId = target.attributes.getNamedItem(
    'data-mitretechnique',
  )?.value
  if (mitreTechniqueId) {
    router.replace({
      query: {
        ...route.query,
        modalTechniqueId: mitreTechniqueId,
      },
    })
  }
}

/**
 * This event handler will attempt to parse the click target for external URLs;
 * If it's external, we pass down props to ExternalLinkModal
 */
const onLinkClickIntercept = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const anchor = target.closest('a')
  if (!anchor?.href) return

  const parsed = isExternalLink(anchor.href)
  if (!parsed) return

  e.preventDefault()
  e.stopPropagation()

  externalLinkUrl.value = parsed.href
  externalLinkOpen.value = true
}

const onConfirmExternalNav = () => {
  if (externalLinkUrl.value) {
    window.open(externalLinkUrl.value, '_blank', 'noopener,noreferrer')
  }
  externalLinkOpen.value = false
}

/**
 * REGISTER GLOBAL EVENT HANDLERS HERE.
 */
onMounted(() => {
  document.addEventListener('mouseup', onClickOfElWithMitreTechniqueAttr)
  document.addEventListener('click', onLinkClickIntercept, {capture: true})
  document.addEventListener('auxclick', onLinkClickIntercept, {capture: true})
})
onUnmounted(() => {
  document.removeEventListener('mouseup', onClickOfElWithMitreTechniqueAttr)
  document.removeEventListener('click', onLinkClickIntercept, {capture: true})
  document.removeEventListener('auxclick', onLinkClickIntercept, {capture: true})
})

</script>

<template>
  <NonAuthenticatedLayout v-if="!authStore.isLoggedIn || route.meta.noLayout">
    <RouterView v-if="route.meta.noLayout" />
  </NonAuthenticatedLayout>
  <AuthenticatedLayout v-else>
    <Suspense>
      <RouterView />
    </Suspense>
  </AuthenticatedLayout>
  <Toaster
    class="pointer-events-auto"
    close-button
    :theme="isDark ? 'dark' : 'light'"
  />
  <ExternalLinkModal v-model:open="externalLinkOpen" :url="externalLinkUrl" @confirm="onConfirmExternalNav" />
</template>

<style scoped></style>
